import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { mattersApi, researchApi } from "@/lib/api";
import { Briefcase, MagnifyingGlass, Scroll, FileText, Plus, ArrowUpRight, CalendarBlank, Sparkle, Clock } from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";

const QUICK = [
    { to: "/research", title: "Research a legal question", icon: MagnifyingGlass, testid: "quick-research" },
    { to: "/drafting?new=1", title: "Draft a notice / reply", icon: Scroll, testid: "quick-draft" },
    { to: "/documents", title: "Upload a PDF for analysis", icon: FileText, testid: "quick-upload" },
    { to: "/matters?new=1", title: "Create a new matter", icon: Briefcase, testid: "quick-new-matter" },
];

export default function Dashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [provider, setProvider] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [s, p] = await Promise.all([mattersApi.dashboard(), researchApi.status().catch(() => null)]);
                setStats(s);
                setProvider(p);
            } catch (e) {
                console.error(e);
            }
            setLoading(false);
        })();
    }, []);

    const greet = () => {
        const h = new Date().getHours();
        if (h < 5) return "शुभ रात्रि";
        if (h < 12) return "Good morning";
        if (h < 17) return "Good afternoon";
        return "Good evening";
    };

    return (
        <div className="p-4 md:p-8 max-w-[1400px] mx-auto fade-in" data-testid="dashboard-page">
            {/* Hero */}
            <div className="mb-8 rule-line pt-3">
                <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-1">Chambers · Today</div>
                <h1 className="font-serif text-4xl md:text-5xl tracking-tight">
                    {greet()},{" "}
                    <span className="italic">{(user?.name || "Advocate").split(" ")[0]}</span>
                </h1>
                <p className="text-muted-foreground mt-2 text-sm md:text-base">
                    Your cause list, research, and drafts — in one chamber.
                </p>
            </div>

            {/* Provider badge */}
            {provider && (
                <div className="mb-6 flex flex-wrap items-center gap-2">
                    <div
                        data-testid="research-provider-badge"
                        className={`chip ${provider.verified ? "chip-external" : "chip-ai"}`}
                        title={provider.note || provider.label}
                    >
                        <Sparkle size={12} weight="fill" /> Research: {provider.label}
                    </div>
                    {!provider.verified && (
                        <div className="text-xs text-muted-foreground">
                            Add <code className="font-mono">PERPLEXITY_API_KEY</code> in backend .env to enable
                            verified-web citations.
                        </div>
                    )}
                </div>
            )}

            {/* Quick actions */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-10">
                {QUICK.map((q) => (
                    <Link
                        key={q.to}
                        to={q.to}
                        data-testid={q.testid}
                        className="group border border-border rounded-md bg-white hover:border-primary/40 hover:shadow-sm transition-all p-4 flex flex-col gap-3"
                    >
                        <div className="flex items-center justify-between">
                            <q.icon size={22} weight="duotone" className="text-primary" />
                            <ArrowUpRight size={16} className="text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                        <div className="text-sm font-medium leading-snug">{q.title}</div>
                    </Link>
                ))}
            </div>

            {/* Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Upcoming hearings */}
                <Card className="lg:col-span-1" data-testid="upcoming-hearings">
                    <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 font-serif text-xl">
                            <CalendarBlank size={18} weight="duotone" /> Upcoming hearings
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        {loading ? (
                            <div className="p-4 space-y-2">
                                <Skeleton className="h-12" />
                                <Skeleton className="h-12" />
                            </div>
                        ) : !stats?.upcoming_hearings?.length ? (
                            <div className="p-6 text-sm text-muted-foreground">No hearings scheduled.</div>
                        ) : (
                            <div className="divide-y divide-border">
                                {stats.upcoming_hearings.map((m) => (
                                    <button
                                        key={m.id}
                                        onClick={() => navigate(`/matters/${m.id}`)}
                                        className="w-full flex items-center justify-between p-4 hover:bg-muted/60 transition-colors text-left"
                                    >
                                        <div className="min-w-0">
                                            <div className="text-sm font-semibold truncate">{m.title}</div>
                                            <div className="text-xs text-muted-foreground truncate">
                                                {m.court || "Court TBD"} · {m.case_number || "No case number"}
                                            </div>
                                        </div>
                                        <div className="text-xs font-mono text-primary shrink-0 pl-3">
                                            {m.hearing_date}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                        <div className="p-3 border-t border-border">
                            <Button variant="ghost" size="sm" onClick={() => navigate("/matters")} className="w-full justify-start text-xs" data-testid="view-all-matters">
                                View all matters →
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Recent docs */}
                <Card data-testid="recent-documents">
                    <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 font-serif text-xl">
                            <FileText size={18} weight="duotone" /> Recent documents
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        {!stats?.recent_documents?.length ? (
                            <div className="p-6 text-sm text-muted-foreground">No documents yet.</div>
                        ) : (
                            <div className="divide-y divide-border">
                                {stats.recent_documents.map((d) => (
                                    <Link
                                        key={d.id}
                                        to={`/documents/${d.id}`}
                                        className="p-4 block hover:bg-muted/60 transition-colors"
                                    >
                                        <div className="text-sm font-medium truncate">{d.original_filename}</div>
                                        <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                                            <span className="chip chip-doc">{d.doc_type || "document"}</span>
                                            <span>{d.language} · {d.num_pages || 0} pages</span>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Recent research */}
                <Card data-testid="recent-research">
                    <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 font-serif text-xl">
                            <MagnifyingGlass size={18} weight="duotone" /> Recent research
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        {!stats?.recent_research?.length ? (
                            <div className="p-6 text-sm text-muted-foreground">
                                No research yet. <Link to="/research" className="text-primary underline">Start a question</Link>.
                            </div>
                        ) : (
                            <div className="divide-y divide-border">
                                {stats.recent_research.map((m) => (
                                    <Link
                                        key={m.id}
                                        to={`/research?memo=${m.id}`}
                                        className="p-4 block hover:bg-muted/60 transition-colors"
                                    >
                                        <div className="text-sm font-medium line-clamp-2">{m.query}</div>
                                        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
                                            <span className={`chip ${m.provider === "perplexity_sonar" ? "chip-external" : "chip-ai"}`}>
                                                {m.provider === "perplexity_sonar" ? "Perplexity" : "Claude"}
                                            </span>
                                            <span>{m.citations?.length || 0} citations</span>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Recent drafts */}
                <Card className="lg:col-span-3" data-testid="recent-drafts">
                    <CardHeader className="pb-3 flex flex-row items-center justify-between">
                        <CardTitle className="flex items-center gap-2 font-serif text-xl">
                            <Scroll size={18} weight="duotone" /> Recent drafts
                        </CardTitle>
                        <Button size="sm" onClick={() => navigate("/drafting?new=1")} data-testid="new-draft-btn">
                            <Plus size={14} /> New draft
                        </Button>
                    </CardHeader>
                    <CardContent className="p-0">
                        {!stats?.recent_drafts?.length ? (
                            <div className="p-6 text-sm text-muted-foreground">
                                No drafts yet.
                            </div>
                        ) : (
                            <div className="divide-y divide-border">
                                {stats.recent_drafts.map((d) => (
                                    <Link
                                        key={d.id}
                                        to={`/drafting/${d.id}`}
                                        className="p-4 flex items-center justify-between hover:bg-muted/60 transition-colors"
                                    >
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium truncate">{d.title}</div>
                                            <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2">
                                                <span className="chip chip-ai">AI draft</span>
                                                <Clock size={11} /> v{d.current_version}
                                            </div>
                                        </div>
                                        <ArrowUpRight size={16} className="text-muted-foreground" />
                                    </Link>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
